import { useEffect, useState } from "react";
import { CandidateLayout } from "@/components/candidate/CandidateLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useUser } from "@supabase/auth-helpers-react";
import { usePageMeta } from "@/hooks/usePageMeta";
import { Gift, Users, Copy, Check, Share2, UserCheck } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

interface Referral {
  id: string;
  referral_code: string;
  referred_id: string | null;
  status: string;
  created_at: string;
  converted_at: string | null;
  referred_name?: string | null;
}

export default function ReferralHistory() {
  usePageMeta({
    title: "Histórico de Convites - Data Hire Club",
    description: "Acompanhe seus convites e veja quem entrou na comunidade através de você",
    noindex: true,
  });

  const user = useUser();
  const { toast } = useToast();
  const [referrals, setReferrals] = useState<Referral[]>([]);
  const [referralCode, setReferralCode] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  const AMBASSADOR_GOAL = 10;
  const convertedCount = referrals.length;
  const progress = Math.min((convertedCount / AMBASSADOR_GOAL) * 100, 100);
  const referralLink = referralCode ? `https://datahireclub.com.br/r/${referralCode}` : null;

  useEffect(() => {
    if (user) {
      fetchReferrals();
    }
  }, [user]);

  const fetchReferrals = async () => {
    if (!user) return;
    setLoading(true);

    try {
      const { data, error } = await supabase
        .from("user_referrals")
        .select("*")
        .eq("referrer_id", user.id)
        .order("created_at", { ascending: false });

      if (error) throw error;

      if (data && data.length > 0) {
        setReferralCode(data[0].referral_code);

        // Filter out seed rows (no referred_id) — only show actual conversions
        const conversionRows = data.filter(r => r.referred_id != null);
        const convertedIds = conversionRows.map(r => r.referred_id);

        if (convertedIds.length > 0) {
          const { data: profiles } = await supabase
            .from("profiles")
            .select("id, full_name")
            .in("id", convertedIds);

          const profileMap = new Map(profiles?.map(p => [p.id, p.full_name]) || []);

          setReferrals(conversionRows.map(r => ({
            ...r,
            referred_name: r.referred_id ? profileMap.get(r.referred_id) : null,
          })));
        } else {
          setReferrals([]);
        }
      }
    } catch (error) {
      console.error("Erro ao buscar referrals:", error);
    } finally {
      setLoading(false);
    }
  };

  const generateReferralCode = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase.rpc("create_user_referral", {
        p_user_id: user.id,
      });

      if (error) throw error;

      setReferralCode(data);
      toast({
        title: "Link criado!",
        description: "Seu link de convite foi gerado com sucesso.",
      });
      fetchReferrals();
    } catch (error) {
      console.error("Erro ao gerar código:", error);
      toast({
        title: "Erro",
        description: "Não foi possível gerar o link de convite.",
        variant: "destructive",
      });
    }
  };

  const copyToClipboard = async () => {
    if (!referralLink) return;

    try {
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);
      toast({
        title: "Copiado!",
        description: "Link copiado para a área de transferência.",
      });
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast({
        title: "Erro",
        description: "Não foi possível copiar o link.",
        variant: "destructive",
      });
    }
  };

  const shareWhatsApp = () => {
    if (!referralLink) return;
    const message = encodeURIComponent(
      `Quer entrar na melhor comunidade de dados do Brasil? 🚀\n\nUse meu link de convite: ${referralLink}`
    );
    window.open(`https://wa.me/?text=${message}`, "_blank");
  };

  return (
    <CandidateLayout maxWidth="4xl">
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-3">
            <Gift className="h-7 w-7 text-primary" />
            Histórico de Convites
          </h1>
          <p className="text-muted-foreground mt-1">
            Acompanhe seus convites e veja quem entrou na comunidade através de você
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-full bg-primary/10">
                  <Users className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{referrals.length}</p>
                  <p className="text-sm text-muted-foreground">Total de convites</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-full bg-green-500/10">
                  <UserCheck className="h-5 w-5 text-green-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{convertedCount}</p>
                  <p className="text-sm text-muted-foreground">Conversões</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Progress to Ambassador */}
        <Card className="bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              🏆 Progresso: Conquista Embaixador
            </CardTitle>
            <CardDescription>
              Convide {AMBASSADOR_GOAL} pessoas para desbloquear
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex justify-between text-sm">
              <span>{convertedCount} de {AMBASSADOR_GOAL} convites convertidos</span>
              <span className="font-medium">{Math.round(progress)}%</span>
            </div>
            <Progress value={progress} className="h-3" />
            {convertedCount >= AMBASSADOR_GOAL && (
              <p className="text-green-600 font-medium">
                🎉 Parabéns! Você é um Embaixador da comunidade!
              </p>
            )}
          </CardContent>
        </Card>

        {/* Referral Link */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Seu Link de Convite</CardTitle>
            <CardDescription>
              Compartilhe este link para convidar pessoas
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!referralCode ? (
              <Button onClick={generateReferralCode} className="w-full sm:w-auto">
                <Gift className="mr-2 h-4 w-4" />
                Gerar Meu Link de Convite
              </Button>
            ) : (
              <div className="space-y-4">
                <div className="flex flex-col sm:flex-row gap-2">
                  <div className="flex-1 bg-muted rounded-md px-4 py-3 text-sm font-mono truncate">
                    {referralLink}
                  </div>
                  <Button variant="outline" onClick={copyToClipboard} className="shrink-0">
                    {copied ? <Check className="h-4 w-4 mr-2" /> : <Copy className="h-4 w-4 mr-2" />}
                    {copied ? "Copiado!" : "Copiar"}
                  </Button>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={shareWhatsApp}>
                    <Share2 className="mr-2 h-4 w-4" />
                    WhatsApp
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Referral List */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Histórico de Convites</CardTitle>
            <CardDescription>
              Todas as pessoas que você convidou
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8 text-muted-foreground">
                Carregando...
              </div>
            ) : referrals.length === 0 ? (
              <div className="text-center py-8">
                <Users className="h-12 w-12 text-muted-foreground/50 mx-auto mb-3" />
                <p className="text-muted-foreground">
                  Você ainda não convidou ninguém
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  Compartilhe seu link para começar!
                </p>
              </div>
            ) : (
              <div className="divide-y">
                {referrals.map((referral) => (
                  <div key={referral.id} className="py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-full bg-green-500/10">
                        <UserCheck className="h-4 w-4 text-green-500" />
                      </div>
                      <div>
                        <p className="font-medium">
                          {referral.referred_name || "Usuário"}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {referral.converted_at
                            ? `Entrou em ${format(new Date(referral.converted_at), "dd/MM/yyyy", { locale: ptBR })}`
                            : `Convite em ${format(new Date(referral.created_at), "dd/MM/yyyy", { locale: ptBR })}`
                          }
                        </p>
                      </div>
                    </div>
                    <Badge variant="default">Convertido</Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </CandidateLayout>
  );
}
